import {getDarkerColor} from '@dv5/components'
import {ReactionWidgetData, StickyNoteData, TableTheme, TableWidgetData, TextWidgetData, Widget, WidgetType} from "@dv5/domain"
import {CSSProperties} from "react"
import {CanvasPosition} from "../model/CanvasPosition"
import {MousePosition} from "../model/MousePosition"
import {toCanvasPosition} from '../containers/utils/canvasViewport'

const TABLE_THEME_STYLES: Record<TableTheme, {background: string, borderColor: string, color: string}> = {
    [TableTheme.LIGHT]: {
        background: 'transparent',
        borderColor: '#e0e0e0',
        color: '#333333',
    },
    [TableTheme.DARK]: {
        background: '#1e1e1e',
        borderColor: '#3a3a3a',
        color: '#e8e8e8',
    },
    [TableTheme.OLD_PAPER]: {
        background: '#f2e8dc',
        borderColor: '#c9bfa8',
        color: '#5b4a32',
    },
    [TableTheme.SOLAR]: {
        background: '#fdf6e3',
        borderColor: '#d3c9b0',
        color: '#657b83',
    },
    [TableTheme.OLD_CONSOLE]: {
        background: '#0c0c0c',
        borderColor: '#1a3a1a',
        color: '#33ff66',
    },
    [TableTheme.AMBER_TERMINAL]: {
        background: '#0a0a0a',
        borderColor: '#3a2a10',
        color: '#ffb000',
    },
    [TableTheme.MIDNIGHT_BLUE]: {
        background: '#0f1b2d',
        borderColor: '#28405e',
        color: '#d6e4f0',
    },
}

class WidgetService {
    getWidgetStyle(widget: Widget): CSSProperties {
        if (widget.type === WidgetType.STICKY_NOTE) {
            const stickyNoteData = widget.data as StickyNoteData
            return {
                left: widget.left.toString() + 'px',
                top: widget.top.toString() + 'px',
                width: widget.width.toString() + 'px',
                height: widget.height.toString() + 'px',
                background: stickyNoteData.bgColor,
                borderColor: getDarkerColor(stickyNoteData.bgColor, 0.2),
                fontStyle: stickyNoteData.fontStyle === 'italic' ? 'italic' : 'normal',
            }
        }
        if (widget.type === WidgetType.TEXT_WIDGET) {
            const textWidgetData = widget.data as TextWidgetData
            const style: CSSProperties = {
                left: widget.left.toString() + 'px',
                top: widget.top.toString() + 'px',
                width: widget.width.toString() + 'px',
                height: widget.height.toString() + 'px',
                background: textWidgetData.bgColor,
                borderColor: getDarkerColor(textWidgetData.bgColor, 0.2),
                color: textWidgetData.fgColor || undefined,
            }

            if (textWidgetData.textProperties) {
                const tp = textWidgetData.textProperties

                if (tp.fontSize) {
                    style.fontSize = `${tp.fontSize}px`
                }
                if (tp.bold) {
                    style.fontWeight = 'bold'
                }
                if (tp.italic) {
                    style.fontStyle = 'italic'
                }

                const textDecorations: string[] = []
                if (tp.underline) {
                    textDecorations.push('underline')
                }
                if (tp.lineThrough) {
                    textDecorations.push('line-through')
                }
                if (textDecorations.length > 0) {
                    style.textDecoration = textDecorations.join(' ')
                }

                if (tp.horizontalTextAlign) {
                    style.textAlign = tp.horizontalTextAlign.toLowerCase() as any
                }
            }

            return style
        }
        if (widget.type === WidgetType.MARKDOWN) {
            return {
                left: widget.left.toString() + 'px',
                top: widget.top.toString() + 'px',
                width: widget.width.toString() + 'px',
                height: widget.height.toString() + 'px',
                background: '#ffffff',
                borderColor: '#e0e0e0',
            }
        }
        if (widget.type === WidgetType.DIAGRAM) {
            return {
                left: widget.left.toString() + 'px',
                top: widget.top.toString() + 'px',
                width: widget.width.toString() + 'px',
                height: widget.height.toString() + 'px',
                background: '#ffffff',
                borderColor: '#e0e0e0',
            }
        }
        if (widget.type === WidgetType.SHAPE) {
            return {
                left: widget.left.toString() + 'px',
                top: widget.top.toString() + 'px',
                width: widget.width.toString() + 'px',
                height: widget.height.toString() + 'px',
            }
        }
        if (widget.type === WidgetType.REACTION) {
            return {
                left: widget.left.toString() + 'px',
                top: widget.top.toString() + 'px',
                width: widget.width.toString() + 'px',
                height: widget.height.toString() + 'px',
            }
        }
        if (widget.type === WidgetType.TABLE) {
            const tableData = widget.data as TableWidgetData
            const tableThemeStyle = (tableData?.theme && TABLE_THEME_STYLES[tableData.theme]) || TABLE_THEME_STYLES[TableTheme.LIGHT]
            return {
                left: widget.left.toString() + 'px',
                top: widget.top.toString() + 'px',
                width: widget.width.toString() + 'px',
                height: widget.height.toString() + 'px',
                background: tableThemeStyle.background,
                borderColor: tableThemeStyle.borderColor,
                color: tableThemeStyle.color,
            }
        }
        return {
            left: widget.left.toString() + 'px',
            top: widget.top.toString() + 'px',
            width: widget.width.toString() + 'px',
            height: widget.height.toString() + 'px',
        }
    }

    getWidgetContentStyle(widget: Widget): CSSProperties {
        const baseStyle: CSSProperties = {
            width: widget.width.toString() + 'px',
            height: widget.height.toString() + 'px',
        }

        if (widget.type === WidgetType.REACTION) {
            baseStyle.display = 'flex'
            baseStyle.alignItems = 'center'
            baseStyle.justifyContent = 'center'
            return baseStyle
        }

        if (widget.type === WidgetType.MARKDOWN) {
            baseStyle.padding = '8px'
            baseStyle.overflow = 'auto'
            return baseStyle
        }

        if (widget.type === WidgetType.DIAGRAM) {
            baseStyle.padding = '8px'
            baseStyle.overflow = 'auto'
            return baseStyle
        }

        if (widget.type === WidgetType.TABLE) {
            baseStyle.padding = '8px'
            baseStyle.overflow = 'auto'
            return baseStyle
        }

        if (widget.type === WidgetType.STICKY_NOTE) {
            return {
                justifyContent: 'center',
                textAlign: 'center',
            }
        }

        if (widget.type === WidgetType.TEXT_WIDGET) {
            const {textProperties} = widget.data as TextWidgetData
            return {
                justifyContent: textProperties?.verticalTextAlign === 'BOTTOM' ? 'flex-end'
                    : textProperties?.verticalTextAlign === 'CENTER' ? 'center' : 'flex-start',
                textAlign: (textProperties?.horizontalTextAlign?.toLowerCase() as CSSProperties['textAlign']) || 'left',
            }
        }

        return baseStyle
    }

    getToolbarStyle(widget: Widget): CSSProperties {
        return {
            left: (widget.left - Math.floor(widget.width / 2)).toString() + 'px',
            top: (widget.top - 56).toString() + 'px',
        }
    }

    getCenteredToolbarStyle(widget: Widget): CSSProperties {
        return {
            left: (widget.left + Math.floor(widget.width / 2)).toString() + 'px',
            top: (widget.top - 56).toString() + 'px',
            transform: 'translateX(-50%)',
        }
    }

    getWidgetClasses(widget: Widget,
                     baseClass: string,
                     selected: boolean,
                     editingWidgetId: string | undefined,
                     hasComments: boolean,
                     ): string {
        const classes = ['widget', baseClass]
        if (selected) {
            classes.push('selected')
        }
        if (editingWidgetId && editingWidgetId === widget.id) {
            classes.push('editing')
        }
        if (hasComments) {
            classes.push('has-comments')
        }
        if (widget.locked) {
            classes.push('locked')
        }
        return classes.join(' ')
    }

    getCanvasPosition(position: MousePosition, canvasElementRect: DOMRect, zoom: number,
                      scrollPosition: CanvasPosition = {left: 0, top: 0}): CanvasPosition {
        return toCanvasPosition(position, canvasElementRect, zoom, scrollPosition)
    }
}

export const widgetService = new WidgetService()
